import { normalStrongStyles } from "../../body/Body.Parts";
import {
  DISMISSIBLE_PADDING_RIGHT,
  DISMISS_AREA_SIZE,
  DISMISS_GAP,
  SELECTION_PADDING_X,
} from "../../selection/Selection.Parts";
import { ROOT_FONT_SIZE_PX, ROW_GAP } from "../Select.Parts";

// TEXT

// STYLES

// DATA

// TYPES
export type Props = {
  selections: (string | number)[];
  getLabelForValue: (value: string | number) => string;
  maxWidth?: string;
  width: string;
  rows?: number;
  search?: boolean;
  small?: boolean;
  anchorEl?: HTMLElement | null;
  containerWidth?: number;
};

// CONSTANTS
const SELECTION_PADDING =
  (SELECTION_PADDING_X + DISMISSIBLE_PADDING_RIGHT) * ROOT_FONT_SIZE_PX;
const DISMISS_WIDTH = (DISMISS_GAP + DISMISS_AREA_SIZE) * ROOT_FONT_SIZE_PX;
const GAP_WIDTH = ROW_GAP * ROOT_FONT_SIZE_PX;

// FUNCTIONS
const measureTextWidth = (text: string): number => {
  const element = document.createElement("span");
  element.style.cssText = `
    position: absolute;
    visibility: hidden;
    font-family: "forma-djr-display", sans-serif;
    ${normalStrongStyles};
  `;
  element.textContent = text;

  document.body.appendChild(element);
  const width = element.offsetWidth;
  document.body.removeChild(element);

  return width;
};

const getSelectionWidth = (label: string): number =>
  measureTextWidth(label) + SELECTION_PADDING + DISMISS_WIDTH;

const widthWith = (rowWidth: number, selectionWidth: number): number =>
  rowWidth + (rowWidth > 0 ? GAP_WIDTH : 0) + selectionWidth;

export const calculateVisibleSelections = (
  selections: (string | number)[],
  maxWidthPx: number,
  rows = 1,
): number => {
  // Width used on each row so far, and the row and width of each placed selection
  const rowWidths = [0];
  const placed: { row: number; width: number }[] = [];

  // Place selections in order, filling each row before moving to the next
  for (const selection of selections) {
    const width = getSelectionWidth(String(selection));
    let row = rowWidths.length - 1;

    // This row is full, so wrap to the next one if there is one
    if (widthWith(rowWidths[row], width) > maxWidthPx) {
      // Stop if the selection doesn't fit even on an empty row, or there are no rows left
      if (rowWidths[row] === 0 || rowWidths.length === rows) break;
      rowWidths.push(0);
      row++;
      if (width > maxWidthPx) break;
    }

    rowWidths[row] = widthWith(rowWidths[row], width);
    placed.push({ row, width });
  }

  // If all selections fit, there's no overflow pill to make room for
  if (placed.length === selections.length) return placed.length;

  // Drop selections from the end until the +N pill fits after the last one
  for (let last = placed.at(-1); last; last = placed.at(-1)) {
    const overflowWidth = getSelectionWidth(
      `+${selections.length - placed.length}`,
    );
    const lastRow = rowWidths.length - 1;
    const fitsOnLastRow =
      widthWith(rowWidths[lastRow], overflowWidth) <= maxWidthPx;
    const fitsOnNextRow =
      rowWidths.length < rows && overflowWidth <= maxWidthPx;

    if (fitsOnLastRow || fitsOnNextRow) break;

    // Not enough room, so hide the last selection; its row closes up, and an emptied row is removed
    placed.pop();
    rowWidths[last.row] = Math.max(
      0,
      rowWidths[last.row] - last.width - GAP_WIDTH,
    );
    if (rowWidths[last.row] === 0 && rowWidths.length > 1) rowWidths.pop();
  }

  return placed.length;
};

// ASSETS
